/**
 * Input Sanitization Utility
 * Provides XSS protection and input validation for user data
 */

import { logger } from './logger';

interface ValidationResult {
  isValid: boolean;
  sanitized: string;
  errors: string[];
}

interface SanitizationOptions {
  allowHtml?: boolean;
  maxLength?: number;
  trimWhitespace?: boolean;
  allowedChars?: RegExp;
}

class InputSanitizer {
  private readonly dangerousPatterns = [
    // SQL injection patterns
    /(\b(SELECT|INSERT|UPDATE|DELETE|DROP|CREATE|ALTER|EXEC|UNION|SCRIPT)\b)/gi,
    /(--|\/\*|\*\/|;)/g,
    // XSS patterns
    /<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi,
    /<iframe\b[^<]*(?:(?!<\/iframe>)<[^<]*)*<\/iframe>/gi,
    /javascript:/gi,
    /on\w+\s*=/gi,
    // Path traversal
    /\.\.\//g,
    /\.\.\\/g,
  ];

  private readonly htmlEntities: Record<string, string> = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#x27;',
    '/': '&#x2F;',
  };

  /**
   * Sanitize a string input with configurable options
   */
  sanitizeString(input: string, options: SanitizationOptions = {}): ValidationResult {
    const {
      allowHtml = false,
      maxLength = 1000,
      trimWhitespace = true,
      allowedChars
    } = options;

    const errors: string[] = [];
    let sanitized = input;

    // Basic type check
    if (typeof input !== 'string') {
      return {
        isValid: false,
        sanitized: '',
        errors: ['Input must be a string']
      };
    }

    // Trim whitespace if requested
    if (trimWhitespace) {
      sanitized = sanitized.trim();
    }

    // Check length
    if (sanitized.length > maxLength) {
      errors.push(`Input exceeds maximum length of ${maxLength} characters`);
      sanitized = sanitized.substring(0, maxLength);
    }

    // Check for dangerous patterns
    for (const pattern of this.dangerousPatterns) {
      if (pattern.test(sanitized)) {
        errors.push('Input contains potentially dangerous content');
        sanitized = sanitized.replace(pattern, '');
        logger.warn('Dangerous pattern detected and removed from input', { pattern: pattern.source });
      }
    }

    // HTML encoding if HTML is not allowed
    if (!allowHtml) {
      sanitized = this.encodeHtml(sanitized);
    }

    // Check allowed characters
    if (allowedChars && !allowedChars.test(sanitized)) {
      errors.push('Input contains invalid characters');
    }

    return {
      isValid: errors.length === 0,
      sanitized,
      errors
    };
  }

  /**
   * Sanitize an object with string values
   */
  sanitizeObject(obj: Record<string, any>, options: SanitizationOptions = {}): Record<string, any> {
    const sanitized: Record<string, any> = {};

    for (const [key, value] of Object.entries(obj)) {
      if (typeof value === 'string') {
        const result = this.sanitizeString(value, options);
        sanitized[key] = result.sanitized;
        
        if (!result.isValid) {
          logger.warn(`Sanitization issues for field ${key}`, { errors: result.errors });
        }
      } else if (Array.isArray(value)) {
        sanitized[key] = value.map(item => 
          typeof item === 'string' 
            ? this.sanitizeString(item, options).sanitized 
            : item
        );
      } else if (value !== null && typeof value === 'object') {
        sanitized[key] = this.sanitizeObject(value, options);
      } else {
        sanitized[key] = value;
      }
    }

    return sanitized;
  }

  /**
   * Encode HTML entities
   */
  private encodeHtml(input: string): string {
    return input.replace(/[&<>"'\/]/g, (char) => this.htmlEntities[char] || char);
  }

  /**
   * Validate email address
   */
  validateEmail(email: string): ValidationResult {
    const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
    
    const sanitized = this.sanitizeString(email, { 
      maxLength: 254,
      allowedChars: /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9.-]+$/
    });

    const isValidFormat = emailRegex.test(sanitized.sanitized);
    
    return {
      isValid: sanitized.isValid && isValidFormat,
      sanitized: sanitized.sanitized,
      errors: [
        ...sanitized.errors,
        ...(isValidFormat ? [] : ['Invalid email format'])
      ]
    };
  }

  /**
   * Validate search query for database safety
   */
  validateSearchQuery(query: string): ValidationResult {
    return this.sanitizeString(query, {
      maxLength: 500,
      allowHtml: false,
      allowedChars: /^[a-zA-Z0-9\s\-_.,!?@#$%&*()+=[\]{}|\\:";'<>\/]*$/
    });
  }

  /**
   * Validate business name
   */
  validateBusinessName(name: string): ValidationResult {
    return this.sanitizeString(name, {
      maxLength: 200,
      allowHtml: false,
      allowedChars: /^[a-zA-Z0-9\s\-_.,!&'()]*$/
    });
  }

  /**
   * Validate address
   */
  validateAddress(address: string): ValidationResult {
    return this.sanitizeString(address, {
      maxLength: 300,
      allowHtml: false,
      allowedChars: /^[a-zA-Z0-9\s\-_.,#()]*$/
    });
  }

  /**
   * Validate phone number
   */
  validatePhoneNumber(phone: string): ValidationResult {
    // Remove common phone formatting characters
    const cleaned = phone.replace(/[\s\-().+]/g, '');
    
    const result = this.sanitizeString(cleaned, {
      maxLength: 20,
      allowHtml: false,
      allowedChars: /^[0-9+]*$/
    });

    // Check if it looks like a valid phone number
    const phoneRegex = /^(\+?1?)?[0-9]{10,14}$/;
    const isValidPhone = phoneRegex.test(result.sanitized);

    return {
      isValid: result.isValid && isValidPhone,
      sanitized: result.sanitized,
      errors: [
        ...result.errors,
        ...(isValidPhone ? [] : ['Invalid phone number format'])
      ]
    };
  }

  /**
   * Validate license number
   */
  validateLicenseNumber(license: string): ValidationResult {
    return this.sanitizeString(license, {
      maxLength: 50,
      allowHtml: false,
      allowedChars: /^[a-zA-Z0-9\-_]*$/
    });
  }

  /**
   * Validate city name
   */
  validateCityName(city: string): ValidationResult {
    return this.sanitizeString(city, {
      maxLength: 100,
      allowHtml: false,
      allowedChars: /^[a-zA-Z\s\-'.,]*$/
    });
  }

  /**
   * Validate county name
   */
  validateCountyName(county: string): ValidationResult {
    return this.sanitizeString(county, {
      maxLength: 100,
      allowHtml: false,
      allowedChars: /^[a-zA-Z\s\-'.,]*$/
    });
  }

  /**
   * Validate numeric input
   */
  validateNumber(input: string | number, min?: number, max?: number): ValidationResult {
    const stringInput = String(input);
    const sanitized = this.sanitizeString(stringInput, {
      allowedChars: /^[0-9.-]*$/,
      maxLength: 20
    });

    if (!sanitized.isValid) {
      return sanitized;
    }

    const num = parseFloat(sanitized.sanitized);
    const errors: string[] = [...sanitized.errors];

    if (isNaN(num)) {
      errors.push('Input is not a valid number');
    }

    if (min !== undefined && num < min) {
      errors.push(`Number must be at least ${min}`);
    }

    if (max !== undefined && num > max) {
      errors.push(`Number must be at most ${max}`);
    }

    return {
      isValid: errors.length === 0,
      sanitized: sanitized.sanitized,
      errors
    };
  }

  /**
   * Sanitize filter object for database queries
   */
  sanitizeFilters(filters: Record<string, any>): Record<string, any> {
    const sanitized: Record<string, any> = {};

    for (const [key, value] of Object.entries(filters)) {
      if (value === null || value === undefined || value === '') {
        continue; // Skip empty values
      }

      switch (key) {
        case 'business_name':
          sanitized[key] = this.validateBusinessName(String(value)).sanitized;
          break;
        case 'premise_city':
        case 'mailing_city':
          sanitized[key] = this.validateCityName(String(value)).sanitized;
          break;
        case 'premise_county':
        case 'mailing_county':
          sanitized[key] = this.validateCountyName(String(value)).sanitized;
          break;
        case 'license_number':
          sanitized[key] = this.validateLicenseNumber(String(value)).sanitized;
          break;
        case 'phone_number':
        case 'google_phone':
          sanitized[key] = this.validatePhoneNumber(String(value)).sanitized;
          break;
        case 'search':
          sanitized[key] = this.validateSearchQuery(String(value)).sanitized;
          break;
        case 'limit':
        case 'offset':
        case 'page':
          const numResult = this.validateNumber(value, 0, 10000);
          if (numResult.isValid) {
            sanitized[key] = parseInt(numResult.sanitized);
          }
          break;
        default:
          // Generic string sanitization for unknown fields
          if (typeof value === 'string') {
            sanitized[key] = this.sanitizeString(value).sanitized;
          } else if (Array.isArray(value)) {
            sanitized[key] = value.map(item => 
              typeof item === 'string' ? this.sanitizeString(item).sanitized : item
            );
          } else {
            sanitized[key] = value;
          }
      }
    }

    return sanitized;
  }

  /**
   * Check if input contains suspicious patterns
   */
  isSuspicious(input: string): boolean {
    return this.dangerousPatterns.some(pattern => pattern.test(input));
  }

  /**
   * Log security violations
   */
  private logSecurityViolation(input: string, violation: string): void {
    logger.warn('Security violation detected', {
      violation,
      inputLength: input.length,
      timestamp: new Date().toISOString()
    });
  }
}

// Export singleton instance
export const sanitizer = new InputSanitizer();

// Export types
export type { ValidationResult, SanitizationOptions };
